import { useState, useEffect } from 'react'
import { useSetOrgParams } from '~/common/use-set-org-params'
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, List, ListItem,
  ListItemText, Stack, TextField, Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useOrgParams } from '~/common/use-org-params'
import { useUpdateOrganization, useDeleteOrganization } from '~/api/supabase-mutations'
import { plotEdited, plotRemoved, useCropsContextDispatch } from '~/common/context/crops/crops-context'
import { useMapRefresh } from '~/map/context/use-map-refresh'
import { supabaseGetter } from '~/api/supabase-getter'
import { usePlotCropCounts } from './use-plot-crop-counts'
import { useUpsertPlotPolygon } from '~/plot/edit-plot/use-upsert-plot-polygon'

/**
 * Right-panel content for the Plot Editor tab in map-fullscreen.jsx.
 *
 * Props:
 *   plotId        — currently selected plot org ID (from URL) or null
 *   plots         — all plots array from usePlotPolygons (includes polygonId)
 *   isDirty       — boolean from usePolygonEdit
 *   currentPoints — array from usePolygonEdit
 *   onDiscard     — reset() from usePolygonEdit
 */
export const MapPlotEditorPanel = ({ plotId, plots, isDirty, currentPoints, onDiscard }) => {
  const { farmId } = useOrgParams()
  const setOrgParams = useSetOrgParams()
  const { enqueueSnackbar } = useSnackbar()
  const { triggerRefresh } = useMapRefresh()
  const cropsDispatch = useCropsContextDispatch()

  const { updateOrganization } = useUpdateOrganization()
  const { upsertPlotPolygon } = useUpsertPlotPolygon()
  const { deleteOrganization } = useDeleteOrganization()

  const cropCountMap = usePlotCropCounts(farmId, plots)

  const selectedPlot = plots?.find(p => p.id === plotId)

  const [plotName, setPlotName] = useState(selectedPlot?.name ?? '')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Sync name field when selected plot changes
  useEffect(() => {
    setPlotName(selectedPlot?.name ?? '')
  }, [selectedPlot?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveName = async () => {
    const { error } = await updateOrganization(plotId, { name: plotName })
    if (error) {
      enqueueSnackbar(error.message, { variant: 'error' })
      return
    }
    supabaseGetter.organizations.invalidate()
    cropsDispatch(plotEdited({ plotId }))
    triggerRefresh()
    enqueueSnackbar('Plot name saved', { variant: 'success' })
  }

  const handleSavePoints = async () => {
    const { error } = await upsertPlotPolygon({ plotId, points: currentPoints })
    if (!error) onDiscard()
  }

  const handleDelete = async () => {
    const { error } = await deleteOrganization(plotId)
    setDeleteDialogOpen(false)
    if (error) {
      enqueueSnackbar(error.message, { variant: 'error' })
      return
    }
    supabaseGetter.organizations.invalidate()
    supabaseGetter.organizationPolygons.invalidate()
    cropsDispatch(plotRemoved({ plotId }))
    triggerRefresh()
    setOrgParams({ farmId })
    enqueueSnackbar('Plot deleted', { variant: 'success' })
  }

  // No plot selected — show all plots with crop counts
  if (!plotId) {
    return (
      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select a plot on the map to edit it.
        </Typography>
        {plots?.length > 0 && (
          <List dense disablePadding>
            {plots.map(plot => {
              const count = cropCountMap.get(plot.id) ?? 0
              return (
                <ListItem key={plot.id} disableGutters sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={plot.name}
                    secondary={`${count} crop${count !== 1 ? 's' : ''}`}
                    slotProps={{ secondary: { variant: 'caption' } }}
                  />
                </ListItem>
              )
            })}
          </List>
        )}
      </Box>
    )
  }

  const cropCount = cropCountMap.get(plotId) ?? 0

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', height: '100%' }}>
      {/* Plot header */}
      <Box>
        <Typography variant="subtitle1" fontWeight={500}>{selectedPlot?.name ?? '…'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {cropCount} crop{cropCount !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Divider />

      {/* Rename */}
      <Stack direction="row" gap={1} alignItems="flex-start">
        <TextField
          label="Plot name"
          value={plotName}
          onChange={e => setPlotName(e.target.value)}
          size="small"
          fullWidth
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSaveName}
          disabled={!plotName || plotName === selectedPlot?.name}
          sx={{ flexShrink: 0, alignSelf: 'center' }}
        >
          Save
        </Button>
      </Stack>

      {/* Boundary editing */}
      <Stack direction="row" gap={1}>
        <Button
          variant="contained"
          size="small"
          disabled={!isDirty}
          onClick={handleSavePoints}
        >
          Save Points
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!isDirty}
          onClick={onDiscard}
        >
          Discard
        </Button>
      </Stack>

      <Divider />

      {/* Delete */}
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => setDeleteDialogOpen(true)}
        sx={{ alignSelf: 'flex-start' }}
      >
        Delete Plot
      </Button>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete "{selectedPlot?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the plot and its polygon boundary. Crops within this plot will not be deleted but will lose their plot association.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
